import React from "react";
import { BlockSkeleton, TextSkeleton } from "../interfaccia/loading";
import {
  formatDateTime,
  formatDuration,
  formatRelativeTime,
  severityConfig,
} from "./cronStatus.utils";

function Metric({ label, value }) {
  return (
    <div className="cron-detail-metric ba bordoColAlt br2 pa2">
      <div className="f7 scrittaColMedia">{label}</div>
      <div className="b mt1">{value ?? 0}</div>
    </div>
  );
}

function CronStatusRow({
  job,
  expanded,
  detail,
  detailLoading,
  detailError,
  onToggle,
  onRequestRun,
}) {
  const severity = severityConfig(job.severity);

  const copyDetail = async () => {
    if (!detail || typeof navigator === "undefined" || !navigator.clipboard) return;
    await navigator.clipboard.writeText(JSON.stringify(detail, null, 2));
  };

  return (
    <article className="cron-status-row sfondoBiancoTema br3 mb2">
      <div className="cron-status-row-main pa3">
        <div className={`cron-status-icon ${severity.colorClass}`}>
          <span className="material-icons" aria-hidden="true">
            {severity.icon}
          </span>
        </div>

        <div className="cron-status-name">
          <div className="b">{job.label}</div>
          <div className="f7 scrittaColMedia mt1">
            {job.group} · {job.enabled === false
              ? job.disabledReason || "Processo disattivato"
              : job.scheduleLabel || "Pianificazione non disponibile"}
          </div>
          {job.headline ? <div className="f7 mt2">{job.headline}</div> : null}
        </div>

        <div className="cron-status-state">
          <div className={`b ${severity.colorClass}`}>{severity.label}</div>
          <div className="f7 scrittaColMedia mt1">{job.status || "N/D"}</div>
        </div>

        <div className="cron-status-time">
          <div>
            {job.activeRun
              ? `Avviato ${formatRelativeTime(job.activeRun.startedAt)}`
              : formatRelativeTime(job.lastRunAt)}
          </div>
          <div
            className="f7 scrittaColMedia mt1"
            title={formatDateTime(job.activeRun?.timeoutAt || job.lastRunAt)}
          >
            {job.activeRun
              ? `Soglia ${formatDateTime(job.activeRun.timeoutAt)}`
              : `Durata ${formatDuration(job.durationMs)}`}
          </div>
        </div>

        <div className="cron-status-actions flex justify-end items-center">
          {job.canRun ? (
            <button
              type="button"
              className="cron-icon-button button-reset bg-transparent bn pointer dark-blue"
              onClick={() => onRequestRun(job)}
              title="Avvia processo"
              aria-label={`Avvia ${job.label}`}
            >
              <span className="material-icons" aria-hidden="true">play_arrow</span>
            </button>
          ) : null}
          <button
            type="button"
            className="cron-icon-button button-reset bg-transparent bn pointer scrittaColMedia"
            onClick={() => onToggle(job.key)}
            title={expanded ? "Nascondi dettagli" : "Mostra dettagli"}
            aria-expanded={expanded}
            aria-label={`${expanded ? "Nascondi" : "Mostra"} dettagli ${job.label}`}
          >
            <span className="material-icons" aria-hidden="true">
              {expanded ? "expand_less" : "expand_more"}
            </span>
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="cron-status-detail bt bordoColAlt pa3">
          {detailLoading ? (
            <div aria-label="Caricamento dettaglio">
              <BlockSkeleton tone="soft" width={160} height={20} className="mb3" />
              <TextSkeleton tone="soft" lines={3} className="mb3" />
              <BlockSkeleton tone="soft" height={120} />
            </div>
          ) : detailError ? (
            <div className="dark-red" role="alert">{detailError}</div>
          ) : detail ? (
            <>
              {detail.activeRun ? (
                <div className={`cron-active-run-alert ba br2 pa3 mb3 ${detail.activeRun.state === "stalled" ? "dark-red" : "dark-blue"}`}>
                  <div className="b">
                    {detail.activeRun.state === "stalled"
                      ? "Esecuzione oltre la soglia prevista"
                      : "Esecuzione attualmente in corso"}
                  </div>
                  <div className="f7 mt2">
                    Avviata: {formatDateTime(detail.activeRun.startedAt)} · Soglia: {formatDateTime(detail.activeRun.timeoutAt)}
                  </div>
                  {detail.activeRun.state === "stalled" ? (
                    <div className="f7 mt2">
                      Il lock resta attivo: non viene liberato automaticamente per evitare esecuzioni sovrapposte.
                    </div>
                  ) : null}
                </div>
              ) : null}
              <div className="flex justify-between items-start mb3">
                <div>
                  <div><strong>Ultima esecuzione:</strong> {formatDateTime(detail.lastRunAt)}</div>
                  <div className="mt1"><strong>Prossima attesa:</strong> {formatDateTime(detail.expectedNextRunAt)}</div>
                  <div className="mt1"><strong>Attesa integrazione:</strong> {formatDuration(detail.waitMs)}</div>
                  {detail.enabled === false ? (
                    <div className="mt1"><strong>Disattivazione:</strong> {detail.disabledReason}</div>
                  ) : null}
                </div>
                <button
                  type="button"
                  className="cron-icon-button button-reset bg-transparent bn pointer scrittaColMedia"
                  onClick={copyDetail}
                  title="Copia dettaglio"
                  aria-label={`Copia dettaglio ${job.label}`}
                >
                  <span className="material-icons" aria-hidden="true">content_copy</span>
                </button>
              </div>

              {detail.metrics ? (
                <div className="cron-detail-metrics mb3">
                  <Metric label="Pendenti" value={detail.metrics.pendingTotal} />
                  <Metric label="Modifiche prodotto" value={detail.metrics.productChanges} />
                  <Metric label="Allineamenti" value={detail.metrics.alignments} />
                  <Metric label="Massive" value={detail.metrics.massive} />
                  <Metric label="VIG" value={detail.metrics.pendingVig} />
                  <Metric label="BOT" value={detail.metrics.pendingBot} />
                </div>
              ) : null}

              <div className="mb3">
                <strong>Messaggio</strong>
                <div className="mt1 break-word">{detail.message || "Nessun messaggio"}</div>
              </div>
              <div className="mb3">
                <strong>Ultimi eventi</strong>
                {detail.historyAvailable === false ? (
                  <div className="mt1 f7 scrittaColMedia">
                    Storico non ancora disponibile in questo ambiente.
                  </div>
                ) : detail.history?.length ? (
                  <div className="mt2 ba bordoColAlt br2">
                    {detail.history.map((run) => (
                      <div key={run.id} className="pa2 bt bordoColAlt cron-history-row">
                        <div className="flex flex-wrap justify-between">
                          <span className="b">{run.status || "N/D"}</span>
                          <span className="f7 scrittaColMedia">
                            {formatDateTime(run.runAt)} · {formatDuration(run.durationMs)}
                          </span>
                        </div>
                        <div className="f7 mt1 break-word">
                          {run.message || "Nessun messaggio"}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mt1 f7 scrittaColMedia">Nessun evento registrato.</div>
                )}
              </div>
              <div>
                <strong>Response</strong>
                <pre className="cron-response pa3 mt2 mb0 br2 sezioneConScroll">
                  {detail.response === null
                    ? "Nessun dettaglio disponibile"
                    : typeof detail.response === "string"
                      ? detail.response
                      : JSON.stringify(detail.response, null, 2)}
                </pre>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export default CronStatusRow;
